import React, { useEffect, useState } from 'react'
import {
  Delete,
  PauseOutlined,
  PlayArrowOutlined,
  EditOutlined,
  CloseOutlined
} from '@mui/icons-material'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import {
  Box,
  Button,
  Popover,
  Tooltip,
  Typography,
  tooltipClasses,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  TextField
} from '@mui/material'
import { Link } from 'react-router-dom'
import { Contracted } from 'src/assets/campaignBlock/Contracted'
import { NewReplied } from 'src/assets/campaignBlock/NewReplied'
import { NewOpened } from 'src/assets/campaignBlock/NewOpened'
import { VerticalMore } from 'src/assets/general/VerticalMore'
import { styled } from '@mui/material/styles'
import {
  useDeleteCampaignMutation,
  usePauseCampaignMutation,
  useRenameCampaignMutation,
  useResumeCampaignMutation
} from 'src/services/campaign-service'
import { toast } from 'react-hot-toast'
import { generateDates } from 'src/utils/util.js'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import RemoveCampaignDialog from './RemoveCampaignDialog'
import { useSelector } from 'react-redux'
import Countdown from 'react-countdown'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import moment from 'moment-timezone'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  Legend
} from 'chart.js'
import DuplicateCampaignModal from './duplicateCampaignModal'
// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  Legend
)

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      mode: 'index',
      intersect: false
    }
  },
  scales: {
    x: {
      grid: {
        display: false
      },
      ticks: {
        color: '#8181B0'
      }
    },
    y: {
      grid: {
        show: true,
        borderColor: '#E6E6E6',
        strokeDashArray: 4,
        position: 'back',
        xaxis: {
          lines: {
            show: false
          }
        },
        yaxis: {
          lines: {
            show: false
          }
        }
      },
      min: 0
    }
  },
  interaction: {
    intersect: false
  }
}

dayjs.extend(utc)
dayjs.extend(timezone)

const EmailAnalyticsTooltip = ({ title, IconComponent, label, value }) => {
  const theme = useTheme()

  const CustomTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: `${theme.palette.primary.primaryText}`,
      color: '#fff',
      boxShadow: theme.shadows[1],
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '15.12px',
      borderRadius: 20,
      marginBottom: 0
    }
  }))
  return (
    <CustomTooltip title={title} placement='top' sx={{ textAlign: 'center' }}>
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 700,
          lineHeight: '17.64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          color: `${theme.palette.primary.primaryText}`
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mr: 1 }}>
          <IconComponent />
        </Box>
        <Box flexDirection='column'>
          <Typography sx={{ fontSize: '13px', fontWeight: '400', color: theme.palette.grey[500] }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: '14px', fontWeight: '700' }}>
            {value}
          </Typography>
        </Box>
      </Typography>
    </CustomTooltip>
  )
}

const CampaignBlockNew = ({ campaign, onCampaignChange, canEdit }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [editOpen, setEditOpen] = useState(false)
  const [editedName, setEditedName] = useState(campaign.name)
  const [openRemoveCampaignDialog, setOpenRemoveCampaignDialog] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const open = Boolean(anchorEl)
  const theme = useTheme()
  const [series, setSeries] = useState({
    labels: ['Nov 9', 'Nov 10', 'Nov 11', 'Nov 12', 'Nov 13', 'Nov 14'],
    datasets: []
  })

  const handleClick = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }
  const handleRename = async () => {
    const data = {
      id: campaign._id,
      params: {
        name: editedName || campaign.name
      }
    }
    const { message } = await renameCampaign(data).unwrap()
    toast.success(message)
    setEditedName(campaign.name)
    setEditOpen(false)
    setAnchorEl(null)
    onCampaignChange(true)
  }

  const [pauseCampaign] = usePauseCampaignMutation()
  const [renameCampaign] = useRenameCampaignMutation()
  const [resumeCampaign] = useResumeCampaignMutation()

  const handlePauseCampaignClick = async (id) => {
    const { message } = await pauseCampaign(id).unwrap()
    onCampaignChange(true)
    toast.success(message)
  }

  const handleResumeCampaignClick = async (id) => {
    onCampaignChange(true)
    try {
      const { message } = await resumeCampaign(id).unwrap()
      toast.success(message)
    } catch (error) {
      toast.error(error.data.error.message)
    }
  }

  const handleClose = () => {
    setAnchorEl(null)
  }
  const calculateDaysDifference = (userDate) => {
    if (userDate) {
      const currentDate = new Date()
      const diffInTime = currentDate - new Date(userDate)
      const diffInDays = Math.floor(diffInTime / (1000 * 3600 * 24))
      const formattedOutput = diffInDays === 1 ? '1 Day' : `${diffInDays} Days`
      return formattedOutput
    }
  }

  const id = open ? 'simple-popover' : undefined

  const [mouseEntered, setMouseEntered] = useState(false)

  const workspace = useSelector((state) => state.workspace)

  useEffect(() => {
    const { start, end, campaignTimeZone } = campaign.analytics
    const labels = []
    const sent = []
    const open = []
    const reply = []
    const linkedinInvitations = []
    const linkedinMessages = []

    const dates = generateDates(start, end, campaignTimeZone)
    for (const date of dates) {
      const formattedDate = moment.tz(date, campaignTimeZone).format('MMM DD')
      const label = formattedDate?.split(' ')
      const data = campaign.analytics.graph.find((data) => data._id === date)
      labels.push(label)
      sent.push(data?.sent ?? 0)
      open.push(data?.open ?? 0)
      reply.push(data?.reply ?? 0)
      linkedinInvitations.push(data?.linkedinInviteSent ?? 0)
      linkedinMessages.push(data?.linkedinMessageSent ?? 0)
    }

    setSeries({
      labels,
      datasets: [{
        label: 'Sent',
        data: sent,
        borderColor: '#4285f4',
        backgroundColor: 'rgba(66, 133, 244, 0.2)',
        fill: 'origin',
        tension: 0,
        pointRadius: 0,
        borderWidth: 2
      },
      {
        label: 'Total Opens',
        data: open,
        borderColor: '#FF7000',
        backgroundColor: 'rgba(177, 75, 11, 0.2)',
        fill: 'origin',
        tension: 0,
        pointRadius: 0,
        borderWidth: 2
      },
      {
        label: 'Total Replies',
        data: reply,
        borderColor: '#34a853',
        backgroundColor: 'rgba(0, 240, 40, 0.2)',
        fill: 'origin',
        tension: 0,
        pointRadius: 0,
        borderWidth: 2
      },
      {
        label: 'Linkedin Invitations',
        data: linkedinInvitations,
        borderColor: '#C867F4',
        backgroundColor: 'rgba(189, 4, 235, 0.2)',
        fill: 'origin',
        tension: 0,
        pointRadius: 0,
        borderWidth: 2
      },
      {
        label: 'Linkedin Messages',
        data: linkedinMessages,
        borderColor: '#FF4500',
        backgroundColor: 'rgba(255, 69, 0, 0.3)',
        fill: true,
        tension: 0,
        pointRadius: 0,
        borderWidth: 2
      }
      ]
    })
  }, [campaign.analytics, workspace])

  const [deleteCampaign] = useDeleteCampaignMutation()

  const handleDeleteCampaignClick = async () => {
    setAnchorEl(null)
    onCampaignChange(true)
    const toastId = toast.loading('Deleting campaign...', { duration: Infinity })
    const { message } = await deleteCampaign(campaign._id).unwrap()
    toast.success(message, { id: toastId, duration: 2000 })
  }

  const handleInfoIconVisibility = (status, schedules = [], campaignSchedules) => {
    let message = null
    let remainingTime = null
    let nextDayTime = null
    const currentDate = dayjs().utc()
    const currentDay = currentDate.format('ddd').toLowerCase()
    const daysOfWeek = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
    const currentDayIndex = daysOfWeek.indexOf(currentDay)

    const validSchedule = campaignSchedules.find(schedule => schedule[currentDay] && schedule?.isDefault)
    if (!validSchedule) {
      const nextDayInfo = findNextDay(currentDayIndex, campaignSchedules, daysOfWeek)
      if (!nextDayInfo && status === 'active') {
        message = 'No active schedules in upcoming days.'
        return { message, remainingTime: null }
      } else if (!nextDayInfo && status !== 'active') {
        return { message, remainingTime: null }
      }
      const nextDayIndex = daysOfWeek.indexOf(nextDayInfo.nextDay)
      const daysUntilNext = (nextDayIndex - currentDayIndex + 7) % 7
      const nextDayDate = currentDate.add(daysUntilNext, 'day')
      nextDayTime = nextDayDate.diff(currentDate, 'milliseconds')
    }

    const sortedSchedules = schedules.map(schedule => ({
      ...schedule,
      From: dayjs(schedule?.From),
      To: dayjs(schedule?.To)
    })).sort((a, b) => a?.From - b?.From)

    const isNextDayTimeInSchedules = sortedSchedules.some(schedule => {
      const nextDayDate = currentDate.add(nextDayTime, 'millisecond')
      return (
        nextDayDate.isSame(schedule.From, 'day') ||
        nextDayDate.isBetween(
          schedule.From.startOf('day'),
          schedule.To.endOf('day'),
          null,
          '[)'
        )
      )
    })

    for (const [index, schedule] of sortedSchedules.entries()) {
      const { From: scheduleFrom, To: scheduleTo } = schedule
      const isLastSchedule = index === sortedSchedules.length - 1
      const isBetween = currentDate.isBetween(scheduleFrom, scheduleTo, null, '[)')
      if ((status === 'active' && isBetween && !nextDayTime) || (status !== 'active')) {
        message = ''
        remainingTime = null
        break
      }

      if (isLastSchedule && currentDate.isAfter(scheduleTo)) {
        message = 'Schedule has been ended.'
        break
      }

      if (currentDate.isSame(scheduleFrom, 'day') && currentDate.isBefore(scheduleTo)) {
        remainingTime = scheduleFrom.diff(currentDate)
        remainingTime = remainingTime + (nextDayTime ?? 0)
        break
      }

      if (currentDate.isBefore(scheduleFrom)) {
        remainingTime = scheduleFrom.diff(currentDate)
        remainingTime = remainingTime + (nextDayTime ?? 0)
        break
      }

      if (status === 'active' && !isNextDayTimeInSchedules) {
        message = 'No scheduled time for this campaign.'
        break
      }
    }

    return { message, remainingTime }
  }

  const findNextDay = (currentDayIndex, campaignSchedules, daysOfWeek) => {
    for (let i = 1; i < 7; i++) {
      const nextDayIndex = (currentDayIndex + i) % 7
      const nextDay = daysOfWeek[nextDayIndex]

      const validSchedule = campaignSchedules.find(schedule => schedule[nextDay] && schedule.isDefault)
      if (validSchedule) {
        return { nextDay, validSchedule }
      }
    }
    return null
  }

  const getCampaignStatus = (status) => {
    if (status === 'completed') {
      return 'Completed'
    } else if (status === 'paused') {
      return 'Paused'
    } else if (status === 'error') {
      return 'Error'
    } else if (status === 'draft') {
      return 'Draft'
    } else {
      return 'Playing'
    }
  }
  const statusText = getCampaignStatus(campaign?.status)
  const statusInfo = handleInfoIconVisibility(campaign?.status, campaign?.activeSchedule, campaign?.schedules)

  const analyticsData = [
    { title: 'Total Emails Contacted', IconComponent: Contracted, label: 'Contacted', value: campaign.analytics?.total?.sent ?? 0 },
    { title: 'Total Emails Opened', IconComponent: NewOpened, label: 'Opened', value: campaign.analytics?.total?.open ?? 0 },
    { title: 'Total Emails Replied', IconComponent: NewReplied, label: 'Replied', value: campaign.analytics?.total?.reply ?? 0 }
  ]
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success'
      case 'active':
        return 'primary'
      case 'error':
        return 'error'
      case 'paused':
        return 'warning'
      default:
        return 'default'
    }
  }

  return (
    <>
      <Link
        to={!mouseEntered && `/campaigns/${campaign._id}`}
        style={{ textDecoration: 'none', color: 'inherit' }}
      >

        <Card
          sx={{

            boxShadow: '0 0 12px 4px rgba(0, 112, 243, 0.4)',
            borderRadius: '12px',

            height: '100%',
            cursor: 'pointer',
            '&:hover': {
              boxShadow: '0 0 12px 4px rgba(0, 112, 243, 0.4)',
              border: '2px solid #0070f3',
              cursor: 'pointer'
            }
          }}
        >
          <CardHeader
            action={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap'
                }}
              >
                <Chip
                  label={campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                  color={getStatusColor(campaign.status)}
                  size='small'
                  sx={{
                    height: '24px',
                    backgroundColor: campaign.status === 'completed' ? '#dcfce7' : undefined,
                    color: campaign.status === 'completed' ? '#166534' : undefined
                  }}
                />

                <Box sx={{ display: 'flex' }}>
                  <Box>
                    <Tooltip
                      title={
                        campaign.status === 'error' && 'Sending accounts has errors, or are paused'
                      }
                      placement='top'
                      arrow
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      />
                    </Tooltip>
                  </Box>
                  {canEdit && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 999,
                        mr: '0px',
                        '&:hover': {
                          backgroundColor: '#f2f2f2'
                        },
                        borderRadius: 0.75,
                        p: 0.75
                      }}
                      onClick={handleClick}
                      onMouseEnter={() => {
                        setMouseEntered(true)
                      }}
                      onMouseLeave={() => {
                        setMouseEntered(false)
                      }}
                    >
                      {' '}
                      <Tooltip title='' placement='top' sx={{ textAlign: 'center' }} arrow>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          {' '}
                          <VerticalMore />
                        </Box>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </Box>
            }
            title={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'flex-start', sm: 'center', md: 'flex-start', lg: 'flex-start' },
                  flexDirection: { xs: 'column', sm: 'column', md: 'column', lg: 'column' },
                  width: '70%',
                  height: '80px'

                }}
              >
                <Typography
                  variant='subtitle1'
                  title={campaign?.name}
                  sx={{
                    display: 'block',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    wordBreak: 'break-all'
                  }}
                >
                  {campaign?.name || '-'}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    flexDirection: 'column'
                  }}
                >
                  {(statusInfo?.remainingTime || statusInfo?.message) && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '2px 5px',
                        backgroundColor: theme.palette.primary.contrastText,
                        borderRadius: '8px',
                        border: '1px solid #e7f0ff',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                        gap: '7px',
                        mt: 1
                      }}
                    >
                      <AccessTimeIcon sx={{ color: theme.palette.primary.primaryText, fontSize: '1rem' }} />
                      {statusInfo?.remainingTime && (
                        <Countdown
                          date={Date.now() + statusInfo?.remainingTime}
                          renderer={({ days, hours, minutes, seconds }) => (
                            <Typography
                              variant='body1'
                              sx={{
                                fontWeight: 500,
                                color: theme.palette.primary.primaryText,
                                fontSize: '15px'
                              }}
                            >
                              Starts in: {days}d {hours}h {minutes}m {seconds}s
                            </Typography>
                          )}
                        />
                      )}
                      {statusInfo?.message && (
                        <Typography
                          variant='body1'
                          sx={{
                            fontWeight: 500,
                            color: theme.palette.primary.primaryText,
                            fontSize: { xs: '15px', sm: '12px', md: '12px', lg: '15px', xl: '15px' },
                            width: { xs: '50vw', sm: '100%', md: '16vw', lg: '16vw', xl: '15vw' }
                          }}
                        >
                          {statusInfo?.message}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {' '}
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 400,
                      lineHeight: '16.38px',
                      color: '#8181B0',
                      mt: 1
                    }}
                  >
                    {calculateDaysDifference(campaign.createdAt)} Ago &nbsp; &nbsp; &nbsp;{' '}
                    {statusText}
                  </Typography>
                </Box>
                {campaign.stats && (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ wordBreak: 'break-word' }}
                  />
                )}
              </Box>
            }
            sx={{ pb: 0 }}
          />
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                pt: 1,
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              {analyticsData.map((data, index) => (
                <EmailAnalyticsTooltip
                  key={index}
                  title={data.title}
                  IconComponent={data.IconComponent}
                  label={data.label}
                  value={data.value}
                />
              ))}
            </Box>
            <Box sx={{ height: { xs: 150, sm: 200 }, width: '100%' }}>
              <Line data={series} options={chartOptions} />
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mt: 2,
                flexWrap: 'wrap',
                justifyContent: { xs: 'center', sm: 'center' }
              }}
            >
              <Typography variant='body2' color='#4285f4'>
                ● Sent
              </Typography>
              <Typography variant='body2' color='#fbbc05'>
                ● Opened
              </Typography>
              <Typography variant='body2' color='#34a853'>
                ● Clicked
              </Typography>
              <Typography variant='body2' color='#ea4335'>
                ● Replied
              </Typography>
            </Box>

          </CardContent>
        </Card>
      </Link>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: 200,
            p: 1
          }}
        >
          {campaign.status !== 'draft' && campaign.status !== 'completed' && (
            <Button
              fullWidth
              sx={{
                py: 1.5,
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                color: '#101828',
                '&:hover': {
                  color: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1)
                },
                borderRadius: 1
              }}
              onClick={
                campaign.status === 'active'
                  ? () => handlePauseCampaignClick(campaign._id)
                  : () => handleResumeCampaignClick(campaign._id)
              }
            >
              {' '}
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                {campaign.status === 'active'
                  ? (
                    <PauseOutlined sx={{ mr: 1 }} fontSize='small' />
                    )
                  : (
                    <PlayArrowOutlined sx={{ mr: 0.5 }} />
                    )}
                {campaign.status === 'active' ? 'Pause Campaign' : 'Resume Campaign'}
              </Box>
            </Button>
          )}

          <Button
            fullWidth
            sx={{
              py: 1.5,
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              color: '#101828',
              '&:hover': {
                color: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.1)
              },
              borderRadius: 1
            }}
            onClick={() => setEditOpen(true)}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
              <EditOutlined sx={{ mr: 1 }} fontSize='small' />
              Rename Campaign
            </Box>
          </Button>
          <Button
            fullWidth
            onClick={() => setModalOpen(true)}
            sx={{
              py: 1.5,
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              color: '#101828',
              '&:hover': {
                color: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.1)
              },
              borderRadius: 1
            }}
            disabled={!canEdit}
          >
            <ContentCopyRoundedIcon sx={{ mr: 1 }} fontSize='small' />
            Duplicate Campaign
          </Button>
          <Button
            fullWidth
            sx={{
              py: 1.5,
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              color: '#101828',
              '&:hover': {
                color: theme.palette.error.main,
                backgroundColor: alpha(theme.palette.error.main, 0.1)
              },
              borderRadius: 1
            }}
            disabled={!canEdit}
          >
            <Box
              sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
              onClick={() => {
                setOpenRemoveCampaignDialog(true)
              }}
            >
              <Delete sx={{ mr: 1 }} fontSize='small' />
              Remove Campaign
            </Box>
          </Button>
        </Box>
      </Popover>
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        sx={{
          '& .MuiPaper-root': {
            width: 300
          }
        }}
      >
        <DialogTitle sx={{ pb: 0 }}>Edit Campaign Name</DialogTitle>
        <IconButton
          onClick={() => setEditOpen(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseOutlined />
        </IconButton>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            onChange={(e) => setEditedName(e.target.value)}
            value={editedName}
            sx={{ width: '100%' }}
          />
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'center', pb: 2 }}>
          <Button onClick={handleRename} variant='contained'>
            Update
          </Button>
        </DialogActions>
      </Dialog>
      <RemoveCampaignDialog
        open={openRemoveCampaignDialog}
        onClose={() => {
          setOpenRemoveCampaignDialog(false)
        }}
        onClick={handleDeleteCampaignClick}
      />
      <DuplicateCampaignModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCampaignChange={onCampaignChange}
        campaignName={campaign.name}
        campaignId={campaign.id}
      />
    </>
  )
}

export default CampaignBlockNew
